import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import {
  ArrowLeft,
  ExternalLink,
  History,
  MessageCircle,
  PanelRightClose,
  Plus,
  Send,
  Square,
  Trash2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { AgentThinkingComic } from '@/webmcp/ui/AgentThinkingComic';
import { ChatReportCard } from '@/components/CentralChat/ChatReportCard';
import { ChatAccessModeSelect } from '@/components/CentralChat/ChatAccessModeSelect';
import { EmjaeTrace } from '@/components/CentralChat/EmjaeTrace';
import { MarkdownContent } from '@/components/CentralChat/MarkdownContent';
import { awaitingLabel, hasSettledTrace } from '@/components/CentralChat/chatActivity';
import { offeredRoutePath } from '@/components/CentralChat/navigation';
import { useCentralChatRuntime } from '@/components/CentralChat/centralChatRuntime';

/**
 * EditorChatPanel — the MJ chat as a right-side, resizable, in-flow panel on the
 * blog editor route.
 *
 * It is NOT a bubble and NOT a fixed overlay: a fixed overlay would cover the
 * 340px inspector rail the operator is chatting about. EditorShell renders it as
 * the last child of its horizontal band, so it coexists with the rail rather
 * than sitting on top of it.
 *
 * The chat body mirrors CentralChatFloatingBubble's compact view. That file is
 * owned by another lane and must stay untouched, so this panel composes the
 * shared runtime (`useCentralChatRuntime`) and the shared render primitives
 * (`MarkdownContent`, `EmjaeTrace`, `ChatReportCard`) rather than editing or
 * reparenting the bubble. The 90s idle watchdog and the deliberately-off
 * cross-tab rehydration both live in the runtime, so composing the hook
 * preserves them for free.
 *
 * Resize chrome follows QANotesDrawer's hand-rolled pattern (no new dependency):
 * a width in state, persisted to localStorage, and a left-edge drag strip. The
 * panel's right edge sits at the window's right edge on the full-bleed editor
 * route, so `window.innerWidth - clientX` is the target width.
 */

const MIN_WIDTH = 340;
const MAX_WIDTH = 680;
const STORAGE_KEY = 'ed-chat-panel-width';
const COLLAPSED_KEY = 'ed-chat-panel-collapsed';
const DEFAULT_WIDTH = 400;

// The panel is `shrink-0`, so every pixel it takes comes out of the document
// canvas — the only shrinkable band. All space is derived from ONE honest
// reservation measured inside EditorShell's flex band (which already EXCLUDES
// the app sidebar, so this is correct whether the sidebar is open or collapsed):
//
//   IN_BAND_RESERVED = inspector rail + mini-rail/collapse column
//   MIN_CANVAS       = the narrowest article column that is still readable
//
// The panel can dock only when `band − IN_BAND_RESERVED − MIN_CANVAS ≥ MIN_WIDTH`;
// below that there is no room for a usable panel AND a usable article at once, so
// it stays a collapsed strip. When it can dock, its width is capped so the canvas
// keeps MIN_CANVAS at every viewport. The old code had two contradictory magic
// constants and a floor that let the panel show anyway, crushing the canvas.
const IN_BAND_RESERVED = 384; // rail 340 + mini/collapse column ~44
const MIN_CANVAS = 480;

/** Widest the panel may open given the current band, or a value < MIN_WIDTH when
 *  it cannot dock without starving the canvas. */
function maxDockWidth(bandWidth: number): number {
  return Math.min(MAX_WIDTH, bandWidth - IN_BAND_RESERVED - MIN_CANVAS);
}

function getStoredWidth(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const w = Number.parseInt(stored, 10);
      if (Number.isFinite(w) && w >= MIN_WIDTH && w <= MAX_WIDTH) return w;
    }
  } catch {
    /* storage unavailable — fall through to default */
  }
  return DEFAULT_WIDTH;
}

/** The operator's explicit collapse choice, or null when they have not set one
 *  (the panel then follows whether it can dock without starving the canvas). */
function getStoredCollapsedPref(): boolean | null {
  try {
    const stored = localStorage.getItem(COLLAPSED_KEY);
    if (stored === '1') return true;
    if (stored === '0') return false;
  } catch {
    /* storage unavailable */
  }
  return null;
}

function tenantDomain(tenantId: string | null): string {
  if (!tenantId) return 'no workspace';
  return tenantId.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

function tenantInitials(tenantId: string | null): string {
  if (!tenantId) return '··';
  const stripped = tenantId.replace(/^https?:\/\//, '').replace(/^www\./, '');
  const head = stripped.split(/[./_-]/)[0] || stripped;
  return head.slice(0, 2).toUpperCase();
}

function relativeTime(iso?: string | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function EditorChatPanel() {
  const chat = useCentralChatRuntime();
  const scrollAnchorRef = useRef<HTMLDivElement | null>(null);
  const [view, setView] = useState<'chat' | 'history'>('chat');
  const [width, setWidth] = useState<number>(getStoredWidth);
  const [isDragging, setIsDragging] = useState(false);
  const [collapsedPref, setCollapsedPref] = useState<boolean | null>(getStoredCollapsedPref);
  // The EditorShell flex band's width (excludes the app sidebar), measured live.
  const [bandWidth, setBandWidth] = useState<number>(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1920,
  );
  const panelRootRef = useRef<HTMLElement | null>(null);

  const dockWidth = maxDockWidth(bandWidth);
  const canDock = dockWidth >= MIN_WIDTH;
  // Collapsed when the operator chose to collapse, or when there is simply no
  // room to dock without starving the canvas (an explicit open cannot override
  // physics). Width is capped so the canvas keeps MIN_CANVAS whenever open.
  const effectiveCollapsed = collapsedPref === true || !canDock;
  const effectiveWidth = Math.min(width, Math.max(MIN_WIDTH, dockWidth));

  const setCollapsed = useCallback((next: boolean) => {
    setCollapsedPref(next);
    try {
      localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
    } catch {
      /* storage unavailable — non-fatal */
    }
  }, []);
  const toggleCollapsed = useCallback(
    () => setCollapsed(!effectiveCollapsed),
    [setCollapsed, effectiveCollapsed],
  );

  useEffect(() => {
    if (view !== 'chat') return;
    scrollAnchorRef.current?.scrollIntoView({ block: 'end' });
  }, [chat.messages, chat.isStreaming, view]);

  // Drag-resize. Width grows as the cursor moves left; the panel's right edge is
  // the window's right edge on this full-bleed route.
  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      const cap = Math.max(MIN_WIDTH, dockWidth);
      const next = Math.min(cap, Math.max(MIN_WIDTH, window.innerWidth - e.clientX));
      setWidth(next);
    };
    const onUp = () => setIsDragging(false);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, dockWidth]);

  useEffect(() => {
    if (isDragging) return;
    try {
      localStorage.setItem(STORAGE_KEY, String(width));
    } catch {
      /* storage unavailable — non-fatal */
    }
  }, [width, isDragging]);

  // Measure the flex band, not the viewport: the band already excludes the app
  // sidebar, so the dock math stays correct whether the sidebar is open or
  // collapsed. EditorShell wraps the panel in a shrink-0 [data-chat-slot] div
  // whose width follows the panel's own content, so measuring it would feed the
  // dock math back its own width. Step out through the slot to the real band; if
  // the wrapper is absent (panel used bare), fall back to the direct parent.
  useEffect(() => {
    const root = panelRootRef.current;
    const band =
      root?.closest('[data-chat-slot]')?.parentElement ?? root?.parentElement;
    if (!band || typeof ResizeObserver === 'undefined') return;
    const measure = () => setBandWidth(band.getBoundingClientRect().width);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(band);
    return () => ro.disconnect();
  }, []);

  const showHistory = useCallback(() => {
    chat.refreshConversations();
    setView('history');
  }, [chat]);

  const openFromHistory = useCallback(
    (conversationId: string) => {
      chat.openConversation(conversationId);
      setView('chat');
    },
    [chat],
  );

  const newChat = useCallback(() => {
    chat.startNew();
    setView('chat');
  }, [chat]);

  if (effectiveCollapsed) {
    // A thin rail that gives the panel's whole width back to the article. The
    // re-open control shows only when there is actually room to dock — on a band
    // too narrow for a usable panel and a usable article, opening is not offered
    // (the full chat lives at /chat), rather than a button that crushes the doc.
    return (
      <div
        ref={panelRootRef as RefObject<HTMLDivElement>}
        className="shrink-0 flex h-full flex-col items-center border-l border-warm-border bg-paper py-2"
      >
        {canDock && (
          <button
            type="button"
            onClick={toggleCollapsed}
            title="Open Emjae chat"
            aria-label="Open Emjae chat"
            className="grid size-8 place-items-center rounded-md text-ink-2 transition-colors hover:bg-paper-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
          >
            <MessageCircle className="size-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <section
      ref={panelRootRef}
      aria-label="Emjae chat"
      className="relative shrink-0 flex h-full min-h-0 flex-col border-l border-warm-border bg-warm-bg text-ink"
      style={{ width: effectiveWidth }}
    >
      {/* Left-edge drag strip. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize chat panel"
        className="group/resize absolute left-0 top-0 bottom-0 z-10 flex w-1.5 cursor-col-resize items-center"
        onMouseDown={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
      >
        <div className="absolute inset-y-0 left-0 w-1.5 bg-transparent transition-colors group-hover/resize:bg-coral/20" />
      </div>

      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-warm-border bg-paper px-3">
        <div className="grid size-8 shrink-0 place-items-center rounded-md border border-warm-border bg-paper-2 font-mono text-[11px] font-semibold">
          {tenantInitials(chat.tenantId)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-display text-[17px] leading-none">Emjae</div>
          <div className="mt-1 truncate font-mono text-[10px] uppercase tracking-wider text-ink-3">
            {tenantDomain(chat.tenantId)} · {chat.contextLabel}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={toggleCollapsed}
            title="Collapse chat"
            aria-label="Collapse chat"
            className="grid size-8 place-items-center rounded-md text-ink-2 transition-colors hover:bg-paper-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
          >
            <PanelRightClose className="size-4" />
          </button>
          <button
            type="button"
            onClick={view === 'history' ? () => setView('chat') : showHistory}
            title={view === 'history' ? 'Back to chat' : 'History'}
            aria-pressed={view === 'history'}
            className="grid size-8 place-items-center rounded-md text-ink-2 transition-colors hover:bg-paper-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
          >
            {view === 'history' ? <ArrowLeft className="size-4" /> : <History className="size-4" />}
          </button>
          <button
            type="button"
            onClick={newChat}
            title="New chat"
            className="grid size-8 place-items-center rounded-md text-ink-2 transition-colors hover:bg-paper-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
          >
            <Plus className="size-4" />
          </button>
          <button
            type="button"
            onClick={chat.openFullChat}
            title="Open full chat"
            className="grid size-8 place-items-center rounded-md text-ink-2 transition-colors hover:bg-paper-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
          >
            <ExternalLink className="size-4" />
          </button>
        </div>
      </header>

      <AgentThinkingComic variant="expanded" />

      {view === 'history' ? (
        <div className="flex-1 overflow-y-auto px-3 py-3">
          <div className="mb-3 flex items-center justify-between">
            <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
              Conversations
            </span>
            <button
              type="button"
              onClick={newChat}
              className="rounded-md border border-warm-border bg-paper px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-ink-2 transition-colors hover:border-warm-border-strong hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
            >
              New chat
            </button>
          </div>
          {chat.conversationLoading ? (
            <p className="px-1 py-6 text-center text-[13px] text-ink-3">Loading conversations...</p>
          ) : chat.conversations.length === 0 ? (
            <p className="px-1 py-6 text-center text-[13px] text-ink-3">No conversations yet</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {chat.conversations.map((conversation) => (
                <li key={conversation.id} className="group flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => openFromHistory(conversation.id)}
                    className={cn(
                      'min-w-0 flex-1 rounded-md px-2 py-2 text-left transition-colors hover:bg-paper-2',
                      conversation.id === chat.activeConversationId && 'bg-paper-2',
                    )}
                  >
                    <span className="block truncate text-[13px] text-ink">
                      {conversation.title || 'MJ chat'}
                    </span>
                    <span className="mt-0.5 block truncate font-mono text-[10px] uppercase tracking-wider text-ink-3">
                      {relativeTime(conversation.last_message_at) ||
                        `${conversation.message_count} messages`}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => chat.deleteConversation(conversation.id)}
                    title="Delete conversation"
                    className="grid size-8 shrink-0 place-items-center rounded-md text-ink-3 opacity-0 transition-all hover:bg-destructive/10 hover:text-destructive focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/30 group-hover:opacity-100"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-3 py-4">
          {chat.messages.length === 0 ? (
            <div className="space-y-4">
              <div>
                <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-teal-dark">
                  <span className="size-1.5 rounded-full bg-teal" aria-hidden="true" />
                  ready
                </div>
                <h2 className="mt-2 font-display text-[24px] leading-tight text-ink">
                  Start from this page.
                </h2>
              </div>
              <div className="grid gap-2">
                {chat.modes.slice(0, 4).map((mode) => (
                  <button
                    key={mode.id}
                    type="button"
                    onClick={() => chat.handleMode(mode)}
                    disabled={!chat.tenantId || chat.isStreaming}
                    className="flex items-center gap-2 rounded-md border border-warm-border bg-paper px-3 py-2 text-left transition-colors hover:border-warm-border-strong hover:bg-paper-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">
                      {mode.number}
                    </span>
                    <span className="text-[13px] text-ink">{mode.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div role="log" aria-live="polite" className="flex flex-col gap-2">
              {chat.messages.map((message) => (
                <article
                  key={message.id}
                  className={cn(
                    'mt-6 flex flex-col gap-2.5 first:mt-0',
                    message.role === 'user' ? 'items-end' : 'items-start',
                  )}
                >
                  <div
                    className={cn(
                      'text-[13px] leading-relaxed',
                      message.role === 'user'
                        ? 'max-w-7/8 rounded-lg bg-coral-light px-3 py-2 text-coral-dark'
                        : 'w-full text-ink',
                    )}
                  >
                    {message.role === 'assistant' ? (
                      <>
                        {chat.isStreaming && message.id === chat.messages.at(-1)?.id ? (
                          <div className="mb-1.5">
                            <EmjaeTrace
                              entries={chat.toolActivity}
                              startedAt={chat.turnStartedAt}
                              status={chat.status || 'Reading…'}
                            />
                          </div>
                        ) : (
                          hasSettledTrace(message) && (
                            <div className="mb-1.5">
                              <EmjaeTrace
                                entries={message.trace ?? []}
                                startedAt={null}
                                durationMs={message.traceDurationMs}
                                outcome={message.traceOutcome}
                              />
                            </div>
                          )
                        )}
                        {message.content.trim() ? (
                          <MarkdownContent content={message.content} />
                        ) : chat.isStreaming && message.id === chat.messages.at(-1)?.id ? null : (
                          <span className="text-ink-3 italic">No response.</span>
                        )}
                      </>
                    ) : (
                      <span className="whitespace-pre-wrap">{message.content}</span>
                    )}
                    {message.role === 'assistant' &&
                      message.quickReplies?.length &&
                      message.id === chat.messages.at(-1)?.id &&
                      !chat.isStreaming && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {message.quickReplies.map((reply) => (
                            <button
                              key={reply}
                              type="button"
                              onClick={() => chat.submit(reply)}
                              disabled={!chat.tenantId}
                              className="rounded-full border border-warm-border bg-paper-2 px-2.5 py-1 text-[12px] text-ink transition-colors hover:border-coral hover:bg-coral-light hover:text-coral-dark disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
                            >
                              {reply}
                            </button>
                          ))}
                        </div>
                      )}
                    {message.reportProposalId && (
                      <div className="mt-2">
                        <ChatReportCard
                          proposalId={message.reportProposalId}
                          tenantId={chat.tenantId}
                          onOpen={() => undefined}
                        />
                      </div>
                    )}
                    {message.actions?.some((action) => offeredRoutePath(action)) && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {message.actions.map((action) => {
                          const path = offeredRoutePath(action);
                          if (!path) return null;
                          return (
                            <button
                              key={action.id}
                              type="button"
                              onClick={() => chat.sandboxOutput({ action: 'navigate', path })}
                              disabled={!chat.tenantId}
                              className="rounded-full border border-warm-border bg-paper-2 px-2.5 py-1 text-[12px] text-ink transition-colors hover:border-coral hover:bg-coral-light hover:text-coral-dark disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
                            >
                              {action.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {message.error && (
                      <div className="mt-2 rounded-md border border-destructive/20 bg-destructive/5 px-2 py-1 font-mono text-[10px] text-destructive">
                        {message.error}
                      </div>
                    )}
                    {message.awaiting && !message.taProposal && (
                      <div
                        role="status"
                        className="mt-2 rounded-md border border-warm-border bg-paper-2 px-2 py-1 font-mono text-[10px] text-ink-2"
                      >
                        {awaitingLabel(message.awaiting)}
                      </div>
                    )}
                  </div>
                </article>
              ))}
              <div ref={scrollAnchorRef} />
            </div>
          )}
        </div>
      )}

      {view === 'chat' && (
        <form
          className="shrink-0 border-t border-warm-border bg-paper p-3"
          onSubmit={(event) => {
            event.preventDefault();
            chat.submit(chat.draft);
          }}
        >
          <div className="rounded-lg border border-warm-border bg-warm-bg focus-within:border-coral focus-within:ring-[3px] focus-within:ring-coral/15">
            <textarea
              aria-label="Message"
              value={chat.draft}
              onChange={(event) => chat.setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  chat.submit(chat.draft);
                }
              }}
              disabled={!chat.tenantId || chat.isStreaming}
              placeholder={chat.tenantId ? 'Ask about this page...' : 'Select a workspace first'}
              rows={2}
              className="block w-full resize-none border-0 bg-transparent px-3 py-2 text-[13px] leading-relaxed outline-none placeholder:text-ink-3 disabled:opacity-60"
            />
            <div className="flex items-center justify-between gap-2 px-2 pb-2">
              <div className="flex min-w-0 items-center gap-2">
                <ChatAccessModeSelect
                  mode={chat.chatAccessMode}
                  onChange={chat.setChatAccessMode}
                  disabled={!chat.tenantId}
                />
                <AgentThinkingComic variant="collapsed" />
                <span
                  className={cn(
                    'size-1.5 rounded-full',
                    chat.isStreaming ? 'animate-pulse bg-teal' : 'bg-ink-3',
                  )}
                  aria-hidden="true"
                />
                <span className="truncate font-mono text-[10px] uppercase tracking-wider text-ink-3">
                  {chat.status || (chat.isStreaming ? 'Streaming' : 'Ready')}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {chat.isStreaming && (
                  <button
                    type="button"
                    onClick={chat.stop}
                    title="Stop"
                    aria-label="Stop"
                    className="grid size-8 place-items-center rounded-full border border-warm-border bg-paper text-ink-2 transition duration-150 hover:border-ink-3 hover:text-ink active:scale-95 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-coral/20"
                  >
                    <Square className="size-3" />
                  </button>
                )}
                <button
                  type="submit"
                  disabled={!chat.draft.trim() || !chat.tenantId || chat.isStreaming}
                  title="Send"
                  className="grid size-8 place-items-center rounded-full bg-primary text-primary-foreground transition duration-150 hover:bg-primary/90 enabled:active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-coral/20"
                >
                  <Send className="size-4" />
                </button>
              </div>
            </div>
          </div>
        </form>
      )}
    </section>
  );
}
